import { useState } from "react";
import { toast } from "sonner";
import { Gift } from "lucide-react";
import { api, cadShort, formatApiErrorDetail } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useStore } from "@/context/StoreContext";

const PRESETS = [25, 50, 100];

export default function GiftCards() {
  const { storeOpen } = useStore();
  const [amount, setAmount] = useState(50);
  const [custom, setCustom] = useState("");
  const [recipient, setRecipient] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const value = custom ? parseFloat(custom) : amount;

  const buy = async () => {
    if (!storeOpen) return toast.error("We're still stocking our shelves — the shop opens soon.");
    if (!value || value <= 0) return toast.error("Please choose an amount");
    setBusy(true);
    try {
      const { data } = await api.post("/giftcard/buy", {
        amount: value,
        recipient_email: recipient || null,
        message,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-16 py-14">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="eyebrow">The gift of choice</p>
            <h1 className="mt-3 font-serif text-4xl lg:text-5xl text-charcoal leading-tight">
              A LuvSprout Gift Card
            </h1>
            <p className="mt-5 text-charcoal/70 leading-relaxed">
              Not sure what they'd love? Let them choose. Our digital gift cards arrive instantly by
              email with a unique code — redeemable on anything in the boutique, with any balance
              saved for next time.
            </p>
            <div className="mt-8 aspect-[3/2] rounded-sm overflow-hidden border border-gold/25 bg-blush relative">
              <div className="absolute inset-0 grid place-items-center text-center p-8">
                <div>
                  <Gift className="h-10 w-10 text-gold mx-auto" />
                  <p className="font-serif text-3xl text-charcoal mt-4">LuvSprout</p>
                  <p className="eyebrow mt-1">Gift Card · {cadShort(value || 0)} CAD</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-soft-border rounded-sm p-8">
            <h2 className="font-serif text-2xl text-charcoal mb-6">Choose an amount</h2>
            <div className="grid grid-cols-3 gap-3">
              {PRESETS.map((p) => (
                <button
                  key={p}
                  data-testid={`giftcard-amount-${p}`}
                  onClick={() => { setAmount(p); setCustom(""); }}
                  className={`py-4 rounded-sm border font-serif text-xl transition-colors ${!custom && amount === p ? "border-gold bg-gold-light text-charcoal" : "border-soft-border text-charcoal/70 hover:border-gold"}`}
                >
                  ${p}
                </button>
              ))}
            </div>
            <div className="mt-4">
              <Label className="text-xs eyebrow text-charcoal/70">Or a custom amount (CAD)</Label>
              <Input data-testid="giftcard-custom" type="number" min="1" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="e.g. 75" className="mt-1.5 h-11 rounded-none border-charcoal/20" />
            </div>
            <div className="mt-6 space-y-4">
              <div>
                <Label className="text-xs eyebrow text-charcoal/70">Recipient email (optional)</Label>
                <Input data-testid="giftcard-recipient" type="email" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="Leave blank to email yourself" className="mt-1.5 h-11 rounded-none border-charcoal/20" />
              </div>
              <div>
                <Label className="text-xs eyebrow text-charcoal/70">Personal message (optional)</Label>
                <Textarea data-testid="giftcard-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="Happy birthday, with love…" className="mt-1.5 rounded-none border-charcoal/20" />
              </div>
            </div>
            <Button data-testid="giftcard-buy-btn" onClick={buy} disabled={busy || !storeOpen} className="w-full mt-6 h-12 bg-charcoal text-white rounded-none hover:bg-gold font-display tracking-wide disabled:opacity-50 disabled:hover:bg-charcoal">
              {!storeOpen ? "Opening Soon" : busy ? "Redirecting…" : `Buy Gift Card · ${cadShort(value || 0)}`}
            </Button>
            <p className="text-[0.7rem] text-center text-charcoal/45 mt-3">Secure payment via Stripe · Test card 4242 4242 4242 4242</p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
